
import React, { useState, useEffect } from 'react';
import { Select, Tag, Spin, Avatar } from 'antd';
import { UserOutlined, ShopOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { crmApi } from '../../../api/client';
import { Contractor, ContractorStatus } from '../../crm/types/contractor';

// Polish status translations and colors
const statusConfig: Record<string, { label: string; color: string }> = {
    ACTIVE: { label: 'Aktywny', color: 'success' },
    POTENTIAL: { label: 'Potencjalny', color: 'processing' },
    BLACKLIST: { label: 'Zablokowany', color: 'error' },
    INACTIVE: { label: 'Nieaktywny', color: 'default' },
};

interface ContractorSelectProps {
    value?: string;
    onChange?: (value: string | undefined, contractor: Contractor | undefined) => void;
    placeholder?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
}

export const ContractorSelect: React.FC<ContractorSelectProps> = ({
    value,
    onChange,
    placeholder = 'Wyszukaj kontrahenta (nazwa, NIP)...',
    style,
    disabled
}) => {
    const [data, setData] = useState<Contractor[]>([]);
    const [fetching, setFetching] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchContractors(searchValue);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchValue]);

    const fetchContractors = async (search: string) => {
        setFetching(true);
        try {
            // If empty search and no value, fetch recent/all? Or just empty.
            // Let's fetch some defaults if empty
            const response = await crmApi.getContractors({
                search,
                limit: 20
            });
            setData(response.data.data);
        } catch (error) {
            console.error('Failed to fetch contractors:', error);
        } finally {
            setFetching(false);
        }
    };

    // If value is set but not in data, we should fetch it to display correctly
    useEffect(() => {
        if (value && !data.find(c => c.id === value)) {
            crmApi.getContractor(value).then(response => {
                const contractor = response.data;
                if (contractor) {
                    setData(prev => {
                        if (prev.find(c => c.id === contractor.id)) return prev;
                        return [...prev, contractor];
                    });
                }
            }).catch(console.error);
        }
    }, [value]);

    const handleChange = async (newValue: string) => {
        if (!newValue) {
            if (onChange) onChange(undefined, undefined);
            return;
        }

        const basicContractor = data.find(d => d.id === newValue);

        // Fetch full contractor details to get the `contacts` relation and other detailed fields
        try {
            const response = await crmApi.getContractor(newValue);
            const fullContractor = response.data;
            if (onChange) {
                onChange(newValue, fullContractor || basicContractor);
            }
        } catch (error) {
            console.error('Failed to fetch full contractor details:', error);
            // Fallback to basic data if fetch fails
            if (onChange) {
                onChange(newValue, basicContractor);
            }
        }
    };

    return (
        <Select
            showSearch
            value={value}
            placeholder={placeholder}
            style={style}
            defaultActiveFirstOption={false}
            filterOption={false}
            onSearch={setSearchValue}
            onChange={handleChange}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            options={data.map(d => ({
                value: d.id,
                label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                            <ShopOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                            {d.name}
                            {d.nip && <span style={{ color: '#999', fontSize: '0.85em', marginLeft: 8 }}>({d.nip})</span>}
                        </span>
                        <span>
                            {d.status === ContractorStatus.BLACKLIST && <CloseCircleOutlined style={{ color: 'red', marginRight: 5 }} />}
                            <Tag color={statusConfig[d.status]?.color || 'default'} style={{ marginRight: 0 }}>
                                {statusConfig[d.status]?.label || d.status}
                            </Tag>
                        </span>
                    </div>
                ),
                contractor: d // passing full obj for direct access if needed
            }))}
            allowClear
            disabled={disabled}
        />
    );
};
